<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import InboxesAPI from 'dashboard/api/inboxes';

export default {
  components: {
    LoadingState,
    SettingsFieldSection,
    NextButton,
    SelectInput,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
      selectedFlowId: null,
      isSavingFlow: false,
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
      flows: 'flowBuilder/getFlows',
    }),
    currentInboxId() {
      return this.inbox?.id || this.$route.params.inboxId;
    },
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](
        this.currentInboxId
      );
    },
    activeFlows() {
      return this.flows.filter(f => f.status === 'active');
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
    },
  },
  mounted() {
    this.fetchBotData();
    this.$store.dispatch('flowBuilder/get');
    this.fetchInboxFlow();
  },

  methods: {
    fetchBotData() {
      this.$store.dispatch('agentBots/get');
      this.$store.dispatch('agentBots/fetchAgentBotInbox', this.currentInboxId);
    },
    async fetchInboxFlow() {
      try {
        const { data } = await InboxesAPI.getInboxFlow(this.currentInboxId);
        this.selectedFlowId = data.flow_id || null;
      } catch {
        // ignore
      }
    },
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
    async saveInboxFlow() {
      this.isSavingFlow = true;
      try {
        await InboxesAPI.setInboxFlow(this.currentInboxId, this.selectedFlowId);
        useAlert(this.$t('FLOW_BUILDER.INBOX_FLOW.SUCCESS_MESSAGE'));
      } catch {
        useAlert(this.$t('FLOW_BUILDER.INBOX_FLOW.ERROR_MESSAGE'));
      } finally {
        this.isSavingFlow = false;
      }
    },
    async disconnectFlow() {
      this.isSavingFlow = true;
      try {
        await InboxesAPI.setInboxFlow(this.currentInboxId, null);
        this.selectedFlowId = null;
        useAlert(this.$t('FLOW_BUILDER.INBOX_FLOW.DISCONNECTED_MESSAGE'));
      } catch {
        useAlert(this.$t('FLOW_BUILDER.INBOX_FLOW.ERROR_MESSAGE'));
      } finally {
        this.isSavingFlow = false;
      }
    },
  },
};
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <template v-else>
      <!-- Agent Bot section -->
      <form @submit.prevent="updateActiveAgentBot">
        <SettingsFieldSection
          :label="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
          :help-text="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
          class="[&>div]:!items-start"
        >
          <SelectInput
            v-model="selectedAgentBotId"
            :placeholder="$t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER')"
            :options="
              agentBots.map(bot => ({ value: bot.id, label: bot.name }))
            "
          />
          <template #extra>
            <div class="grid grid-cols-1 lg:grid-cols-8 mt-3">
              <div class="col-span-1 lg:col-span-2 invisible" />
              <div class="col-span-1 lg:col-span-6 flex gap-2 mx-1">
                <NextButton
                  type="submit"
                  :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
                  :is-loading="uiFlags.isSettingAgentBot"
                />
                <NextButton
                  type="button"
                  :disabled="!selectedAgentBotId"
                  :is-loading="uiFlags.isDisconnecting"
                  faded
                  ruby
                  @click="disconnectBot"
                >
                  {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
                </NextButton>
              </div>
            </div>
          </template>
        </SettingsFieldSection>
      </form>

      <!-- Flow Builder section -->
      <form class="mt-6" @submit.prevent="saveInboxFlow">
        <SettingsFieldSection
          :label="$t('FLOW_BUILDER.INBOX_FLOW.TITLE')"
          :help-text="$t('FLOW_BUILDER.INBOX_FLOW.DESC')"
          class="[&>div]:!items-start"
        >
          <SelectInput
            v-model="selectedFlowId"
            :placeholder="$t('FLOW_BUILDER.INBOX_FLOW.SELECT_PLACEHOLDER')"
            :options="activeFlows.map(f => ({ value: f.id, label: f.name }))"
          />
          <template #extra>
            <div class="grid grid-cols-1 lg:grid-cols-8 mt-3">
              <div class="col-span-1 lg:col-span-2 invisible" />
              <div class="col-span-1 lg:col-span-6 flex gap-2 mx-1">
                <NextButton
                  type="submit"
                  :label="$t('FLOW_BUILDER.INBOX_FLOW.SUBMIT')"
                  :is-loading="isSavingFlow"
                />
                <NextButton
                  type="button"
                  :disabled="!selectedFlowId"
                  :is-loading="isSavingFlow"
                  faded
                  ruby
                  @click="disconnectFlow"
                >
                  {{ $t('FLOW_BUILDER.INBOX_FLOW.DISCONNECT') }}
                </NextButton>
              </div>
            </div>
          </template>
        </SettingsFieldSection>
      </form>
    </template>
  </div>
</template>
